// oxlint-disable max-statements
import { useEffect, useLayoutEffect, useMemo } from 'react';

import { useMachine, useSelector } from '@xstate/react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';

import { useKnownAvailableParams, useNavigate } from '@change-corgi/core-react/router';

import { useStartAPetitionPageData } from '../../pageData';
import { createLogic } from '../orchestrationLogic';
import type { StateContext } from '../types';
import { assertNavigationEvent, buildSnapshot } from '../utils';

import { useInitialDraft } from './useInitialDraft';
import { useSubscribeToMachine } from './useSubscribeToMachine';

type Matches = (value: unknown) => boolean;

type StateOptions = {
	currentDraft?: StateContext['draft'];
	onRemount: (draft: StateContext['draft']) => void;
};
export function useStartAPetitionState({ currentDraft, onRemount }: StateOptions) {
	const navigate = useNavigate();
	const navigationType = useNavigationType();
	const { pathname, search } = useLocation();
	const { step } = useKnownAvailableParams<{ step: string }>();
	const pageData = useStartAPetitionPageData();
	const isSap3NavigationExperiment = pageData.data.experiments.starfishSap3Navigation.variation === 'variant';

	const savedDraft = useInitialDraft(pageData.data.draft);
	const initialDraft = currentDraft ?? savedDraft;

	const restoredSnapshot = useMemo(() => buildSnapshot(initialDraft, step), [initialDraft, step]);

	const machine = createLogic();
	const [, send, actorRef] = useMachine(machine, {
		snapshot: restoredSnapshot,
	});

	const draft = useSelector(actorRef, (s) => s.context.draft);
	const children = useSelector(actorRef, (s) => s.children);

	const activeChildRef = Object.values(children)[0];
	const snapshot = useSelector(activeChildRef, (s) => s);

	// oxlint-disable-next-line typescript/no-unsafe-type-assertion - We know that the snapshot will have a matches function, but it can't be typed correctly due to the dynamic nature of child actors.
	const matches = (value: typeof snapshot.value) => (snapshot as unknown as { matches: Matches }).matches(value);

	useSubscribeToMachine(actorRef);

	/**
	 * Handles synchronizing the state machine with the URL
	 * when the user navigates using the browser's back/forward buttons
	 */
	useEffect(() => {
		const stepFromSnapshot = snapshot.value;

		// if the user navigates using the browser's back/forward buttons,
		// we need to ensure the state machine is synchronizes the machine with the URL
		if (navigationType === NavigationType.Pop && stepFromSnapshot !== step) {
			const updatedDraft = snapshot.context.draft;
			onRemount(updatedDraft);
		}
	}, [navigationType, step, navigationType, onRemount, activeChildRef]);

	/**
	 * Handles synchronizing the URL with the state machine
	 * when transitions occur within the machine (e.g. user clicks "Next" button)
	 */
	useLayoutEffect(() => {
		const subscription = actorRef.system.inspect((event) => {
			try {
				assertNavigationEvent(event);
				// oxlint-disable-next-line typescript/no-unsafe-assignment
				const currentSnapshot = event.actorRef.getSnapshot();
				// oxlint-disable-next-line typescript/no-unsafe-argument, typescript/no-unsafe-member-access
				if (Object.keys(currentSnapshot?.children ?? {}).length !== 0) return;

				const targetStep = event._transitions[0]?.target[0]?.key;
				if (typeof targetStep !== 'string') return;

				const pathnameWithoutStep = pathname.split('/').slice(0, -1).join('/');
				navigate(`${pathnameWithoutStep}/${targetStep}${search}`, { replace: isSap3NavigationExperiment });
			} catch {
				// do nothing if the event is not a navigation event
			}
		});

		return () => subscription.unsubscribe();
	}, [pathname, search, navigate, isSap3NavigationExperiment, actorRef]);

	return {
		actions: { send, matches },
		data: { draft },
	};
}
